package fr.frux.shared.dto

import kotlinx.serialization.Serializable
import kotlin.time.Instant

@Serializable
data class TreeDto(
    val id: Long? = null,
    // Rupture assumee du contrat (plan 2, lot 5) : etait `species: String` en texte libre.
    // Pas de production, donc pas de client deploye a menager.
    val species: SpeciesRefDto,
    val latitude: Double,
    val longitude: Double,
    val status: TreeStatusDto,
    val description: String? = null,
    // Thumb du héro — alimenté pour les listes et la carte. Le détail complet vient de [photos].
    val photoUrl: String? = null,
    // Détail seulement (jamais alimenté sur les listes/map, pour rester léger).
    val photos: List<TreePhotoDto>? = null,
    val createdBy: Long,
    // Pseudo du créateur, renseigné sur le détail (join Users) ; null sur les listes/map
    // où le créateur n'est pas exposé. Nullable = rétro-compatible côté sérialisation.
    val createdByUsername: String? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val updatedAt: Instant? = null,
    val traits: List<TreeTraitSummary>? = null,
    val latestObservation: TreeStatusObservationDto? = null,
    // Passage humain date (ADR 0025). Renseigne aussi sur les listes et findNearby, a la difference
    // de `latestObservation` qui reste du detail. `null` = aucun humain n'a jamais rien observe.
    // Aucun bareme dans le contrat : les niveaux se derivent cote presentation.
    @Serializable(with = InstantEpochMillisSerializer::class)
    val lastObservedAt: Instant? = null,
    val myObservationStatus: TreeStatusDto? = null,
    // Partie recoltable calculee cote serveur a partir du referentiel d'especes et du mois courant.
    // Null quand l'espece ne declare aucune fenetre de recolte. Nullable = retro-compatible.
    val harvestPart: HarvestPartDto? = null,
    // Certitude d'identification (#129). Defaut = retro-compatible.
    val certainty: CertaintyDto = CertaintyDto.PROBABLE,
    // Favori de l'appelant authentifie. Renseigne sur le detail et la liste de favoris ;
    // laisse a false sur les listes cartographiques, qui ne le consomment pas.
    val isFavorite: Boolean = false,
    // Signalement par les pairs (#129 lot B, ADR 0015). Derive de `trees.disputed_at`, jamais de
    // `certainty` : la declaration du createur n'est jamais reecrite par un signalement. Vaut
    // meme vocabulaire visuel que TO_CONFIRM cote carte (pastille "?").
    val disputed: Boolean = false,
    // Kinds des signalements encore ouverts sur l'arbre (#208) : de quoi differencier le bandeau
    // (« identification contestee » vs « signale disparu ») la ou `disputed` ne dit que « conteste ».
    // Chaines et non enum, comme `TreeReportRequest.kind` (un kind inconnu du client s'ignore, repli
    // sur le libelle generique). Renseigne sur le detail seulement, vide sur les listes — meme
    // portee que `reportedByMe`. Defaut = retro-compatible.
    val disputedKinds: List<String> = emptyList(),
    // `true` si l'appelant authentifie a un signalement MISIDENTIFIED encore ouvert sur cet arbre —
    // gouverne "Signaler un doute" vs "Retirer mon signalement" cote fiche. Renseigne sur le detail
    // seulement (meme portee que `isFavorite`) ; false pour un visiteur anonyme ou sur les listes.
    val reportedByMe: Boolean = false,
    // Meme derivation que `reportedByMe`, pour le kind GONE (#129 lot C, ADR 0015) : `reportedByMe`
    // reste scope a MISIDENTIFIED (nom herite du lot B, contrat deja publie) plutot que d'etre
    // renomme en profondeur ou generalise en liste de kinds -- un booleen jumeau, au meme patron que
    // `isFavorite`, est le choix le plus simple qui reste honnete sur ce qu'il represente.
    val reportedGoneByMe: Boolean = false,
    // Droit de correction de l'appelant (#616, modele wiki de la spec 2026-10-19) : vrai pour tout
    // compte connecte, faux pour un anonyme. Renseigne sur le detail et les reponses de mutation
    // qui le remplacent, faux sur les listes.
    val canEdit: Boolean = false,
    // Derniere correction d'identite visible du lecteur (spec 2026-10-19), detail et mutations
    // seulement. Nullable = retro-compatible.
    val lastCorrection: LastCorrectionDto? = null,
    // Lieu de cueillette auquel l'arbre appartient (lot 1 des lieux). Null = arbre isole. Additif (ADR 0034).
    val siteId: Long? = null,
)

@Serializable
data class CreateTreeRequest(
    // L'espece se choisit dans le referentiel (`GET /api/v1/species`), elle ne se saisit plus.
    // Le crochet du chantier B (`proposedSpeciesName: String? = null`) viendra avec l'ecran
    // d'ajout : un champ nullable ajoute est retro-compatible, il ne re-cassera pas le contrat.
    val speciesId: Long,
    val latitude: Double,
    val longitude: Double,
    // Facultatif depuis la spec 2026-10-19 (validation assouplie, additif au sens de l'ADR 0034) :
    // un etat observable devient une observation de terrain de l'auteur, `UNKNOWN` ou l'absence
    // creent l'arbre en `UNKNOWN`. `UNAVAILABLE`, que seuls les builds publies envoient encore,
    // garde son effet d'avant : arbre cree `UNAVAILABLE`, sans observation.
    val status: TreeStatusDto? = null,
    val description: String? = null,
    // Certitude d'identification (#129). Defaut = retro-compatible.
    val certainty: CertaintyDto = CertaintyDto.PROBABLE,
)

@Serializable
data class UpdateTreeRequest(
    val speciesId: Long? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val status: TreeStatusDto? = null,
    /**
     * Sémantique PATCH à sentinelle (#254) : `null` = champ non fourni, inchangé ; chaîne vide
     * (après trim serveur) = effacement. La sérialisation JSON ne distingue pas « absent » de
     * « null explicite », la chaîne vide est donc le seul moyen d'effacer sans casser les
     * clients publiés, qui envoient déjà `null` pour « inchangé ».
     */
    val description: String? = null,
    /**
     * Correction de l'identification par son créateur (#143) : `null` = champ non fourni,
     * inchangé — comme [speciesId]/[status], pas de sentinelle d'effacement, `certainty` n'a
     * pas d'état « vide ». N'entre pas en contradiction avec l'ADR 0015 (« `certainty` n'est
     * jamais réécrite par un signalement ») : cette règle vise `tree_reports`, un tiers qui ne
     * doit jamais corrompre la déclaration. Ici c'est le créateur qui corrige sa propre
     * déclaration, ce que l'ADR nomme explicitement comme le cas qui reste ouvert
     * (« #129 reste ouverte pour la moitié signalement… », commentaire d'issue #143).
     */
    val certainty: CertaintyDto? = null,
)

/**
 * Corps de `POST /api/v1/trees/{id}/reports` (#129 lot B, ADR 0015).
 *
 * [kind] est une chaîne, pas un enum fermé : la base (V18) accepte déjà `GONE` pour le lot C sans
 * migration supplémentaire, et le contrat client ne doit pas être redéployé pour ça. Seul
 * `MISIDENTIFIED` est traité par le service à ce stade — toute autre valeur rend 400 INVALID_KIND.
 */
@Serializable
data class CreateTreeReportRequest(
    val kind: String,
    val suggestedSpeciesId: Long? = null,
)

/** Corps de `POST /api/v1/trees/{id}/reports/resolve` (#129 lot B) — créateur seul. */
@Serializable
enum class TreeReportResolveActionDto { MAINTAIN, }

@Serializable
data class ResolveTreeReportsRequest(
    val action: TreeReportResolveActionDto,
)

@Serializable
data class TreeListResponse(
    val trees: List<TreeDto>,
    val count: Int,
    val total: Int? = null,
    val page: Int? = null,
    val limit: Int? = null,
)

/** Reponse generique a message court, pour les gestes sans corps riche (favoris). */
@Serializable
data class MessageResponse(
    val message: String,
)
